import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import Badge from "@/components/base/Badge";
import Avatar from "@/components/base/Avatar";
import { formatDate, formatMoney } from "@/utils/format";
import { useAuth } from "@/contexts/AuthContext";
import { getEnrollmentPieces, setEnrollmentPiece } from "@/services/ficheService";
import type { EnrollmentPiece } from "@/types/fiche";
import type { SchoolClass, Student } from "@/types";
import { ENROLLMENT_STATUS_LABEL, ENROLLMENT_STATUS_TONE } from "@/utils/enrollment";
import { useStudentDossier } from "@/hooks/useStudentDossier";

interface Props {
  open: boolean;
  onClose: () => void;
  student: Student | null;
  classes: SchoolClass[];
  activeYear: string;
}

/** Dossier complet de l'élève, chargé en un appel (responsables, inscriptions et frais calculés par le serveur). */
export default function DossierModal({ open, onClose, student, classes, activeYear }: Props) {
  const { can } = useAuth();
  const [pieces, setPieces] = useState<EnrollmentPiece[]>([]);
  const { dossier, error } = useStudentDossier(student?.id ?? null, open);
  const current = dossier?.enrollments.find(
    ({ enrollment: e }) => e.schoolYear === activeYear && e.status !== "CANCELLED",
  );
  const currentEnrollmentId = current?.enrollment.id ?? null;

  useEffect(() => {
    if (!currentEnrollmentId) {
      setPieces([]);
      return;
    }
    getEnrollmentPieces(currentEnrollmentId).then(setPieces).catch(() => setPieces([]));
  }, [currentEnrollmentId]);

  if (!student) return null;

  const className = (id: string) => classes.find((c) => c.id === id)?.name ?? "—";
  const guardianLinks = dossier?.guardians ?? [];
  const history = dossier?.enrollments ?? [];
  const requiredDossiers = pieces.map((p) => ({ id: p.pieceId, label: p.label }));
  const dossierState: Record<string, boolean> = Object.fromEntries(pieces.map((p) => [p.pieceId, p.provided]));
  const providedCount = pieces.filter((p) => p.provided).length;
  const canMark = can("students:update");
  const toggleDossier = async (dossierId: string) => {
    if (!currentEnrollmentId || !canMark) return;
    const next = !dossierState[dossierId];
    setPieces((prev) => prev.map((p) => (p.pieceId === dossierId ? { ...p, provided: next } : p)));
    setPieces(await setEnrollmentPiece(currentEnrollmentId, dossierId, next));
  };
  const currentBalance = current?.balance;
  const currentPayments = currentBalance?.lines ?? [];
  const totalDue = currentBalance?.totalDue ?? 0;
  const totalPaid = currentBalance?.totalPaid ?? 0;

  return (
    <Modal open={open} onClose={onClose} title="Dossier élève" subtitle={student.matricule} icon="ri-folder-user-line" size="xl">
      <div className="space-y-6">
        <div className="flex flex-wrap items-start gap-4">
          <Avatar lastName={student.lastName} firstName={student.firstName} src={student.photo || undefined} size="lg" />
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold font-heading text-foreground-950">
              {student.firstName} {student.lastName}
            </h3>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge tone="primary" icon="ri-hashtag">
                {student.matricule}
              </Badge>
              <Badge tone="secondary" icon="ri-genderless-line">
                {student.gender === "M" ? "Masculin" : "Féminin"}
              </Badge>
              <Badge tone="neutral" icon="ri-cake-2-line">
                {formatDate(student.birthDate)}
              </Badge>
              <Badge tone="accent" icon="ri-calendar-check-line">
                Année {activeYear}
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { icon: "ri-map-pin-2-line", label: "Lieu de naissance", value: student.birthPlace || "—" },
            { icon: "ri-home-4-line", label: "Adresse", value: student.address || "—" },
            { icon: "ri-phone-line", label: "Téléphone", value: student.phone || "—" },
          ].map((row) => (
            <div key={row.label} className="rounded-md border border-background-200 bg-background-100/50 px-3.5 py-2.5">
              <p className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-foreground-400">
                <i className={row.icon}></i>
                {row.label}
              </p>
              <p className="mt-0.5 text-sm text-foreground-800">{row.value}</p>
            </div>
          ))}
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
            Responsables ({guardianLinks.length})
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {guardianLinks.map(({ link, guardian }) => (
              <div key={guardian.id} className="flex items-center justify-between gap-3 rounded-md border border-background-200 px-3.5 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar lastName={guardian.lastName} firstName={guardian.firstName} size="sm" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">
                      {guardian.firstName} {guardian.lastName}
                    </p>
                    <p className="text-xs text-foreground-500 truncate">
                      {guardian.profession || "—"} • {guardian.phone}
                    </p>
                  </div>
                </div>
                {link.isPrimary && <Badge tone="primary">Principal</Badge>}
              </div>
            ))}
            {guardianLinks.length === 0 && (
              <p className="rounded-md border border-dashed border-background-300 py-4 text-center text-xs text-foreground-400 sm:col-span-2">
                {dossier ? "Aucun responsable enregistré." : error ?? "Chargement du dossier..."}
              </p>
            )}
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-400">
            Historique année par année
          </p>
          <div className="overflow-hidden rounded-md border border-background-200">
            <table className="w-full text-sm">
              <thead className="bg-background-100">
                <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-3.5 py-2 font-medium">Année</th>
                  <th className="px-3.5 py-2 font-medium">Classe</th>
                  <th className="px-3.5 py-2 font-medium">Date</th>
                  <th className="px-3.5 py-2 font-medium">Statut</th>
                  <th className="px-3.5 py-2 font-medium text-right">Payé</th>
                  <th className="px-3.5 py-2 font-medium text-right">Reste</th>
                </tr>
              </thead>
              <tbody>
                {history.map(({ enrollment: e, balance }) => (
                  <tr key={e.id} className="border-t border-background-200">
                    <td className="px-3.5 py-2.5 font-medium text-foreground-900">{e.schoolYear}</td>
                    <td className="px-3.5 py-2.5 text-foreground-700">{className(e.classId)}</td>
                    <td className="px-3.5 py-2.5 text-foreground-500">{formatDate(e.enrollmentDate)}</td>
                    <td className="px-3.5 py-2.5">
                      <Badge tone={ENROLLMENT_STATUS_TONE[e.status]}>{ENROLLMENT_STATUS_LABEL[e.status]}</Badge>
                    </td>
                    <td className="px-3.5 py-2.5 text-right text-primary-700">{formatMoney(balance.totalPaid)}</td>
                    <td className="px-3.5 py-2.5 text-right text-accent-700">
                      {e.status === "CANCELLED" ? "—" : formatMoney(balance.remaining)}
                    </td>
                  </tr>
                ))}
                {!dossier && (
                  <tr>
                    <td colSpan={6} className="px-3.5 py-4 text-center text-xs text-foreground-400">
                      {error ?? "Chargement du dossier..."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
              Dossiers à fournir ({activeYear})
            </p>
            {requiredDossiers.length > 0 && (
              <Badge tone={providedCount === requiredDossiers.length ? "success" : "danger"}>
                {providedCount}/{requiredDossiers.length} fournis
              </Badge>
            )}
          </div>
          {requiredDossiers.length === 0 ? (
            <p className="rounded-md border border-dashed border-background-300 py-4 text-center text-xs text-foreground-400">
              {currentEnrollmentId ? "Aucune pièce exigée pour la classe de cette année." : "Pas d'inscription active cette année."}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {requiredDossiers.map((d) => {
                const provided = !!dossierState[d.id];
                return (
                  <button
                    key={d.id}
                    type="button"
                    disabled={!canMark}
                    onClick={() => toggleDossier(d.id)}
                    className={`flex items-center gap-3 rounded-md border px-3.5 py-2.5 text-left transition-colors cursor-pointer disabled:cursor-default ${
                      provided ? "border-primary-300 bg-primary-50" : "border-background-200 hover:border-background-300"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                        provided ? "border-primary-500 bg-primary-600 text-white" : "border-background-300 text-transparent"
                      }`}
                    >
                      <i className="ri-check-line text-xs leading-none"></i>
                    </span>
                    <span className={`text-sm ${provided ? "text-foreground-900" : "text-foreground-600"}`}>
                      {d.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
              Frais de l'année en cours ({activeYear})
            </p>
            <Badge tone={totalPaid >= totalDue ? "success" : "danger"}>
              Reste {formatMoney(Math.max(0, totalDue - totalPaid))}
            </Badge>
          </div>
          <div className="overflow-hidden rounded-md border border-background-200">
            <table className="w-full text-sm">
              <thead className="bg-background-100">
                <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="px-3.5 py-2 font-medium">Frais</th>
                  <th className="px-3.5 py-2 font-medium text-right">Dû</th>
                  <th className="px-3.5 py-2 font-medium text-right">Payé</th>
                  <th className="px-3.5 py-2 font-medium text-right">Reste</th>
                </tr>
              </thead>
              <tbody>
                {currentPayments.map((p) => (
                  <tr key={p.key} className="border-t border-background-200">
                    <td className="px-3.5 py-2.5 text-foreground-800">
                      {p.label}
                      {p.dueDate && <span className="ml-1.5 text-xs text-foreground-400">{p.dueDate}</span>}
                    </td>
                    <td className="px-3.5 py-2.5 text-right text-foreground-700">{formatMoney(p.due)}</td>
                    <td className="px-3.5 py-2.5 text-right text-primary-700">{formatMoney(p.paid)}</td>
                    <td className="px-3.5 py-2.5 text-right text-accent-700">{formatMoney(p.remaining)}</td>
                  </tr>
                ))}
                {currentPayments.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3.5 py-4 text-center text-xs text-foreground-400">
                      Aucun frais enregistré pour cette année.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
}